import {
  extractRoute,
  normalizeRoutePath,
  redactAndTruncate,
} from './drop-telemetry';
import type { MinimalEvent, ProcessorDropInfo } from './processor-drop-types';
import { EXCEPTION_TYPE_LENGTH } from './processor-drop-types';

export function isThenable(
  value: unknown,
): value is PromiseLike<MinimalEvent | null | undefined> {
  return (
    typeof (value as { then?: unknown } | null | undefined)?.then === 'function'
  );
}

/** Build the sanitized payload. Same hygiene helper as the `beforeSend` half. */
export function buildProcessorDropInfo(args: {
  processor: string;
  event: MinimalEvent;
  environment: string;
  budgetExhausted?: boolean;
  signatureExhausted?: boolean;
}): ProcessorDropInfo {
  const {
    processor,
    event,
    environment,
    budgetExhausted = false,
    signatureExhausted = false,
  } = args;
  const exception = event.exception?.values?.[0];

  // `event.message` is set for console captures and `captureMessage`;
  // `exception.value` for thrown errors. Either can carry arbitrary upstream
  // text, so both go through the shared redact-then-truncate helper.
  const rawMessage = event.message ?? exception?.value ?? '';

  return {
    processor,
    eventType: event.type ?? 'error',
    messagePrefix: redactAndTruncate(String(rawMessage)),
    exceptionType: redactAndTruncate(
      String(exception?.type ?? ''),
      EXCEPTION_TYPE_LENGTH,
    ),
    route: resolveRoute(event),
    environment,
    budgetExhausted,
    signatureExhausted,
  };
}

/**
 * URL of the request an event belongs to, as early in the chain as it exists.
 *
 * On the server `event.request` is written by `requestDataIntegration`, which
 * runs AFTER `InboundFilters` (`@sentry/node-core` `sdk/index.js`). A processor
 * that drops before RequestData therefore sees no `request.url`. The same URL
 * is already on `sdkProcessingMetadata.normalizedRequest`, copied from the
 * isolation scope before any processor runs (`utils/prepareEvent.js:40-47`),
 * so that is the fallback. Without it such a drop is never refused as local
 * and always reports route `unknown`.
 */
export function requestUrl(
  event: MinimalEvent | undefined,
): string | undefined {
  return (
    event?.request?.url ?? event?.sdkProcessingMetadata?.normalizedRequest?.url
  );
}

function resolveRoute(event: MinimalEvent): string {
  const url = requestUrl(event);
  if (url) {
    return extractRoute(url, event.tags?.routePath);
  }
  // Browser events frequently carry no `request.url` before the SDK's own
  // processors have run, and this instrumentation sits at the HEAD of the
  // chain by design, so the live location is the more reliable source here.
  if (typeof window !== 'undefined' && window.location?.pathname) {
    return normalizeRoutePath(window.location.pathname);
  }
  return 'unknown';
}
